import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAppSelector } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { isInternalUserEmail } from "@faro-lotv/foreign-observers";
import { ApiResponseError } from "@faro-lotv/service-wires";
import { useCallback } from "react";

/**
 * Webhook entry-point defined by this Microsoft PowerAutomate flow
 *
 * The feedback is logged to the "Product Feedback" Teams channel and additionally to an Excel sheet.
 * https://faro01.atlassian.net/wiki/spaces/SWEB/pages/5734432816/Feedback+collection
 */
export const FEEDBACK_WEBHOOK =
  "https://3c7a39625bbae1b7b3592f0f9e8b8b.08.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/74f797e72fde472093053dc21536ce33/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=HEd5SJ02fmMm23oXXg5L7ysVo8CzJlSQhsvIAIzaq4w";

/**
 * Features that we collect feedback for.
 * The values are shown as the title of the feedback.
 */
export enum ProductFeedbackFeature {
  pointCloudAnalysis = "Point cloud analysis",
  floorPlanGeneration = "Floor plan generation",
  xgRedirectToHb = "Sphere XG redirect to HoloBuilder",
}

/** The context attached to every feedback */
type FeedbackContext = {
  /** The email of the user giving feedback. */
  email?: string;

  /** Whether the feedback is from an internal user. */
  isInternal: boolean;

  /** A link to the current project */
  projectLink: string;
};

/**
 * @returns a callback to submit feedback, showing a toast when the operation fails.
 *  It resolves to whether the submission was successful.
 */
export function useSubmitFeedback<T extends object>(): (
  feedbackContent: T,
) => Promise<boolean> {
  const { handleErrorWithToast } = useErrorHandlers();

  const email = useAppSelector(selectCurrentUser)?.email;

  return useCallback(
    async (feedbackContent: T) => {
      const context: FeedbackContext = {
        projectLink: window.location.href,
        email,
        isInternal: isInternalUserEmail(email),
      };

      try {
        await postFeedback({ ...feedbackContent, ...context });

        return true;
      } catch (error) {
        handleErrorWithToast({
          title: "Could not submit feedback. Please try again later.",
          error,
        });

        return false;
      }
    },
    [email, handleErrorWithToast],
  );
}

/**
 * @param feedbackContent the content of the feedback to submit
 */
async function postFeedback(feedbackContent: FeedbackContext): Promise<void> {
  const response = await fetch(FEEDBACK_WEBHOOK, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(feedbackContent),
  });

  if (!response.ok) {
    throw new ApiResponseError(
      response.status,
      response.statusText,
      response.body,
    );
  }
}
